import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ExternalLink, X, Sparkles, TrendingUp, Gift } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { motion, AnimatePresence } from "framer-motion";

const AffiliateAd = () => {
  const { affiliates } = useData();
  const [isMinimized, setIsMinimized] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // ✅ Page-wise memory state (resets on refresh)
  const [closedPages, setClosedPages] = useState<Record<string, boolean>>({});

  const location = useLocation();
  const isAffiliatedPage = location.pathname === "/affiliated";

  // Get current page
  const getCurrentPage = () => {
    const path = location.pathname;
    if (path === '/' || path === '') return 'home';
    if (path.startsWith('/ipos')) return 'ipos';
    if (path.startsWith('/ipo-gmp')) return 'ipo-gmp';
    if (path.startsWith('/news')) return 'news';
    if (path.startsWith('/jobs')) return 'jobs';
    if (path.startsWith('/learning')) return 'learning';
    if (path.startsWith('/refer-earn')) return 'refer-earn';
    return 'home';
  };

  const currentPage = getCurrentPage();

  // ✅ Close only for current page
  const handleCloseCompletely = () => {
    setClosedPages(prev => ({
      ...prev,
      [currentPage]: true
    }));
  };

  const isCompletelyHidden = closedPages[currentPage] || false;

  // Filter affiliates
  const filteredAffiliates = affiliates.filter(affiliate =>
    affiliate.showOnPages && affiliate.showOnPages.includes(currentPage)
  );

  const featuredAffiliate = filteredAffiliates?.[0];

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 2000);
    return () => clearTimeout(timer);
  }, []);

  if (isAffiliatedPage || !featuredAffiliate || !isVisible || isCompletelyHidden) return null;

  return (
    <motion.div
      initial={{ opacity: 0, x: 100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      className="fixed mt-4 top-24 right-4 z-40 w-72 bg-gradient-to-br from-white via-purple-50 to-blue-50 rounded-xl shadow-xl border border-purple-200 overflow-hidden"
    >
      {/* Animated Border */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 opacity-10 animate-pulse"></div>
      
      {/* Header */}
      <Link 
        to="/"
        className="relative block bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 text-white p-3 hover:opacity-95 transition-all duration-300"
      >
        <div className="absolute top-1 left-1">
          <Sparkles className="w-3 h-3 text-yellow-300 animate-pulse" />
        </div>
        <div className="absolute top-1 right-1">
          <Sparkles className="w-3 h-3 text-yellow-300 animate-pulse" />
        </div>
        
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="relative">
              <TrendingUp className="w-4 h-4" />
              <div className="absolute -top-1 -right-1 w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
            </div>
            <div>
              <span className="font-bold text-xs">🔥 HOT DEAL</span>
              <div className="text-xs opacity-90">Limited Offer</div>
            </div>
          </div>

          <button
            onClick={(e) => {
              e.preventDefault();
              handleCloseCompletely();
            }}
            className="text-white/80 hover:text-white transition-colors p-1 rounded-full hover:bg-white/20"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </Link>

      {/* Content */}
      {!isMinimized && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="relative p-3"
        >
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full font-bold animate-bounce">
            NEW
          </div>

          {featuredAffiliate.imageUrl && (
            <div className="mb-3 relative">
              <img 
                src={featuredAffiliate.imageUrl} 
                alt={featuredAffiliate.text}
                className="w-full h-28 object-cover rounded-lg shadow-md"
                onError={(e) => {
                  e.currentTarget.src = "/placeholder.svg";
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-lg"></div>
            </div>
          )}
          
          <div className="mb-3">
            <h3 className="font-bold text-gray-800 text-sm mb-1.5 line-clamp-2 leading-tight">
              🎯 {featuredAffiliate.text}
            </h3>
            <div className="flex items-center gap-2">
              <span className="inline-block px-2 py-0.5 bg-gradient-to-r from-purple-500 to-blue-500 text-white text-xs rounded-full font-bold shadow">
                {featuredAffiliate.type.toUpperCase()}
              </span>
              <div className="flex items-center gap-1 text-xs text-green-600 font-semibold">
                <TrendingUp size={10} />
                TRENDING
              </div>
            </div>
          </div>

          <a
            href={featuredAffiliate.url}
            target="_blank"
            rel="noopener noreferrer"
            className="relative block w-full bg-gradient-to-r from-purple-600 via-blue-600 to-purple-600 text-white text-center py-2 px-3 rounded-lg font-bold text-xs hover:shadow-md transform hover:scale-105 transition-all duration-300 overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse"></div>
            <span className="relative flex items-center justify-center gap-1.5">
              <Gift size={12} />
              CLAIM OFFER NOW
              <Sparkles size={12} className="animate-pulse" />
            </span>
          </a>

          <div className="mt-2 text-center">
            <div className="text-xs text-red-600 font-semibold animate-pulse">
              ⏰ Expires in 24h!
            </div>
          </div>

          <div className="mt-3 pt-2 border-t border-purple-200">
            <p className="text-xs text-gray-600 leading-relaxed text-center">
              <span className="font-semibold">✨ Special Promotion</span> - Don't miss out!
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AffiliateAd;
